import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const PRIMARY = '#2DBCAF';

interface Setting {
  key: string;
  value: any;
  description: string;
}

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 24px;
`;

const SettingCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const SettingInfo = styled.div`
  flex: 1;
  h3 {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 4px;
  }
  p {
    font-size: 12px;
    color: #64748b;
    margin: 0;
  }
  code {
    font-size: 10px;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    color: #475569;
  }
`;

const Toggle = styled.button<{ $active: boolean }>`
  width: 52px;
  height: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  position: relative;
  background: ${p => p.$active ? PRIMARY : '#e2e8f0'};
  transition: background 0.2s;

  &::after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: ${p => p.$active ? '27px' : '3px'};
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${p => p.$active ? '#dcfce7' : '#fee2e2'};
  color: ${p => p.$active ? '#16a34a' : '#dc2626'};
  margin-left: 8px;
`;

const LoadingText = styled.p`
  text-align: center;
  color: #64748b;
  padding: 48px;
`;

// Setting labels
const SETTING_LABELS: Record<string, string> = {
  'immunization.anjuran.premium': 'Imunisasi Anjuran (IDAI) - Premium Only',
};

export default function SettingsPage() {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/admin/settings', { credentials: 'include' });
      const data = await res.json();
      if (data.ok) {
        setSettings(data.settings);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const toggleSetting = async (key: string, currentValue: boolean) => {
    setSaving(key);
    try {
      const res = await fetch(`/api/admin/settings/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: !currentValue }),
      });
      
      if (res.ok) {
        setSettings(prev => prev.map(s => 
          s.key === key ? { ...s, value: !currentValue } : s
        ));
      }
    } catch (err) {
      console.error('Failed to update setting:', err);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <LoadingText>Memuat pengaturan...</LoadingText>;
  }

  return (
    <PageWrapper>
      <Title>⚙️ Pengaturan Aplikasi</Title>
      <Subtitle>Kelola fitur dan pengaturan aplikasi</Subtitle>

      {settings.map(setting => (
        <SettingCard key={setting.key}>
          <SettingInfo>
            <h3>
              {SETTING_LABELS[setting.key] || setting.key}
              <StatusBadge $active={setting.value}>
                {setting.value ? 'ON' : 'OFF'}
              </StatusBadge>
            </h3>
            <p>{setting.description}</p>
            <code>{setting.key}</code>
          </SettingInfo>
          <Toggle 
            $active={setting.value} 
            onClick={() => toggleSetting(setting.key, setting.value)}
            disabled={saving === setting.key}
          />
        </SettingCard>
      ))}
    </PageWrapper>
  );
}
